import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { ICEPACK_FILLER_FORM_NAME, REQUEST_ERROR_MESSAGE, FIELDS_FOR_VALIDATE } from './constants';
import {
	getIcepackFillerInfoRequest,
	createIcepackFillerRequest,
	updateIcepackFillerRequest,
	deleteIcepackFillerRequest,
} from '../../api/requests';
import {
	GET_ICEPACK_FILLER_INFO,
	setIcepackFillerInfoAction,
	setIsIcepackFillerInfoLoadingAction,
	CREATE_ICEPACK_FILLER,
	setIsIcepackFillerCreatingAction,
	UPDATE_ICEPACK_FILLER,
	setIsIcepackFillerUpdatingAction,
	DELETE_ICEPACK_FILLER,
	setIsIcepackFillerDeletingAction,
} from './actions';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import { getDataForSaveSelector, getFormIsInvalidSelector } from './selectors';
import { getIcepacksFillersAction } from '../icepacks-fillers/actions';
import { transformToValidationErrors } from './utils';

export function* getIcepackFillerInfoSaga({ payload: id }) {
	try {
		yield put(setIsIcepackFillerInfoLoadingAction(true));

		const { data, message, toast } = yield call(getIcepackFillerInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setIcepackFillerInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackFillerInfoLoadingAction(false));
	}
}

export function* createIcepackFillerSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackFillerCreatingAction(true));

		const dataForSave = yield select(getDataForSaveSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(createIcepackFillerRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			redirect();

			yield put(getIcepacksFillersAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(ICEPACK_FILLER_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(ICEPACK_FILLER_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackFillerCreatingAction(false));
	}
}

export function* updateIcepackFillerSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackFillerUpdatingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(updateIcepackFillerRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setIcepackFillerInfoAction(data));

			redirect();

			yield put(getIcepacksFillersAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(ICEPACK_FILLER_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(ICEPACK_FILLER_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackFillerUpdatingAction(false));
	}
}

export function* deleteIcepackFillerSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackFillerDeletingAction(true));

		const { data, message, toast } = yield call(deleteIcepackFillerRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();

			yield put(getIcepacksFillersAction({ searchQuery, noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackFillerDeletingAction(false));
	}
}

export default function* icepackFillerModalSaga() {
	yield takeEvery(GET_ICEPACK_FILLER_INFO, getIcepackFillerInfoSaga);
	yield takeEvery(CREATE_ICEPACK_FILLER, createIcepackFillerSaga);
	yield takeEvery(UPDATE_ICEPACK_FILLER, updateIcepackFillerSaga);
	yield takeEvery(DELETE_ICEPACK_FILLER, deleteIcepackFillerSaga);
}
